import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { ProductItem, ProductVariant } from "@/lib/supabase";
import { useShop } from "@/store/ShopContext";
import { getSwatchColor, isColorVariant } from "@/lib/colorSwatch";

interface ProductCardProps {
  product: ProductItem;
  variants?: ProductVariant[];
}

const SHOP_OWNERS = [
  "Ateliê do Cerrado",
  "Mãos de Barro",
  "Fio & Linho",
  "Casa de Cera",
  "Terra & Fibra",
  "Oficina Raiz",
  "Aromas do Campo",
  "Olaria da Serra",
];

export function getShopName(id: number): string {
  return SHOP_OWNERS[id % SHOP_OWNERS.length];
}

export function getRating(id: number): { rating: number; count: number } {
  const r = 4.5 + ((id * 13) % 6) / 10; // 4.5 - 5.0
  const count = ((id * 37) % 380) + 12;
  return { rating: Math.min(5, r), count };
}

export const currencySymbol = (code: string) => {
  if (code === "CNY") return "¥";
  if (code === "EUR") return "€";
  if (code === "GBP") return "£";
  if (code === "USD") return "US$";
  // Default — inclui BRL e qualquer moeda não reconhecida
  return "R$";
};

export const formatPrice = (n: number) =>
  n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Parcelamento estilo Camicado: até 6x sem juros (mínimo R$ 30 por parcela)
export function getInstallment(total: number): { count: number; value: number } | null {
  if (total < 60) return null;
  const maxParcels = 6;
  const minParcelValue = 30;
  let count = maxParcels;
  while (count > 1 && total / count < minParcelValue) {
    count -= 1;
  }
  if (count < 2) return null;
  return { count, value: total / count };
}

export default function ProductCard({ product, variants = [] }: ProductCardProps) {
  const { isFavorite, toggleFavorite, showToast } = useShop();
  const liked = isFavorite(product.id);

  const { mainImg, hoverImg, currentPrice, originalPrice, hasDiscount } = useMemo(() => {
    const media = Array.isArray(product.media) ? product.media : [];
    const mainImg = media[0]?.url ?? "";
    const hoverImg = media[1]?.url ?? media[0]?.url ?? "";
    const priceNum = product.price != null ? Number(product.price) : 0;
    const discNum = product.discount_price != null ? Number(product.discount_price) : 0;
    const hasDiscount = product.discount_enabled && discNum > 0 && discNum < priceNum;
    return {
      mainImg,
      hoverImg,
      currentPrice: hasDiscount ? discNum : priceNum,
      originalPrice: priceNum,
      hasDiscount,
    };
  }, [product]);

  const shopName = getShopName(product.id);
  const { rating, count } = getRating(product.id);
  const isSoldOut = product.stock != null && product.stock <= 0;
  const isLowStock = !isSoldOut && product.stock != null && product.stock > 0 && product.stock <= 6;
  const freeShip = product.id % 3 === 0;
  const symbol = currencySymbol(product.currency);

  // Seleciona a variante de cor (se houver) para os swatches
  const colorVariant = useMemo(
    () => variants.find((v) => isColorVariant(v.name)) ?? null,
    [variants],
  );
  const swatches = useMemo(() => {
    if (!colorVariant) return [];
    return colorVariant.options
      .map((opt) => ({ name: opt, color: getSwatchColor(opt) }))
      .filter((s): s is { name: string; color: string } => s.color !== null);
  }, [colorVariant]);

  // Outros eixos de variação (tamanhos / aromas) — mostrado como selo
  const otherVariantBadge = useMemo(() => {
    if (variants.length === 0) return null;
    const other = variants.find((v) => !isColorVariant(v.name));
    if (!other) return null;
    const n = other.options.length;
    if (n <= 1) return null;
    const plural = other.name.toLowerCase() === "tamanho" ? "tamanhos" : `${other.name.toLowerCase()}s`;
    return `${n} ${plural}`;
  }, [variants]);

  const onToggleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product.id);
    showToast(liked ? "Removido dos favoritos" : "Salvo nos favoritos");
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className={`relative aspect-square rounded-md overflow-hidden bg-background-100 ring-1 ring-background-200/60 group-hover:ring-2 group-hover:ring-foreground-900/80 transition-all ${isSoldOut ? "saturate-50" : ""}`}>
        {mainImg && (
          <img
            src={mainImg}
            alt={product.name}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 group-hover:opacity-0 ${isSoldOut ? "opacity-70" : ""}`}
          />
        )}
        {hoverImg && hoverImg !== mainImg && (
          <img
            src={hoverImg}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        )}
        {hoverImg === mainImg && (
          <div className="absolute inset-0 group-hover:scale-[1.04] transition-transform duration-500"></div>
        )}

        {/* Overlay de informações no hover (rodapé) */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          {swatches.length > 0 && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-background-50/95 backdrop-blur w-fit pointer-events-auto">
              {swatches.slice(0, 5).map((s) => (
                <span
                  key={s.name}
                  title={s.name}
                  className="w-3.5 h-3.5 rounded-full ring-1 ring-foreground-900/15"
                  style={{ backgroundColor: s.color }}
                ></span>
              ))}
              {swatches.length > 5 && (
                <span className="text-[10px] font-medium text-foreground-700 leading-none">
                  +{swatches.length - 5}
                </span>
              )}
            </div>
          )}
          {otherVariantBadge && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background-50/95 backdrop-blur text-[10.5px] font-medium text-foreground-800 w-fit">
              <i className="ri-ruler-line text-[11px]"></i>
              {otherVariantBadge}
            </span>
          )}
        </div>

        {/* Selo de ver detalhes no hover */}
        {!isSoldOut && (
          <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-foreground-950/90 text-background-50 text-[10.5px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <i className="ri-eye-line text-[11px]"></i>
            Ver detalhes
          </span>
        )}

        {/* Overlay de esgotado */}
        {isSoldOut && (
          <>
            <div className="absolute inset-0 bg-background-50/55 pointer-events-none"></div>
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11.5px] font-semibold tracking-wide uppercase pointer-events-none">
              <i className="ri-shopping-bag-3-line text-[13px]"></i>
              Esgotado
            </span>
            <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/95 text-foreground-700 text-[10.5px] font-medium">
              <i className="ri-time-line text-[11px]"></i>
              Em breve novamente
            </span>
          </>
        )}

        <button
          type="button"
          onClick={onToggleLike}
          aria-label={liked ? "Remover dos favoritos" : "Salvar nos favoritos"}
          className={`absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-background-50 flex items-center justify-center hover:bg-background-100 transition-all cursor-pointer ${
            liked ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
          }`}
        >
          <i
            className={`${
              liked ? "ri-heart-fill text-primary-500" : "ri-heart-line text-foreground-900"
            } text-[18px]`}
          ></i>
        </button>
      </div>

      <div className="pt-2.5 px-0.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-[13.5px] text-foreground-900 leading-snug line-clamp-1 flex-1 group-hover:underline">
            {product.name}
          </h3>
          <div className="flex items-center gap-1 shrink-0 text-[12px] text-foreground-700">
            <span className="font-medium text-foreground-900">{rating.toFixed(1)}</span>
            <i className="ri-star-fill text-foreground-900 text-[11px]"></i>
            <span className="text-foreground-500">({count})</span>
          </div>
        </div>

        <p className="text-[12px] text-foreground-500 mt-0.5 truncate">{shopName}</p>

        <div className="mt-1 flex items-baseline gap-2">
          <span className={`text-[15px] font-semibold ${isSoldOut ? "text-foreground-500" : hasDiscount ? "text-primary-600" : "text-foreground-950"}`}>
            {symbol}
            {formatPrice(currentPrice)}
          </span>
          {hasDiscount && (
            <span className="text-[12px] text-foreground-500 line-through">
              {symbol}
              {formatPrice(originalPrice)}
            </span>
          )}
          {isSoldOut && (
            <span className="text-[11px] font-medium text-foreground-600 uppercase tracking-wide">
              · Esgotado
            </span>
          )}
        </div>

        {/* Linha de swatches persistente */}
        {swatches.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1">
            {swatches.slice(0, 4).map((s) => (
              <span
                key={s.name}
                title={s.name}
                className="w-3 h-3 rounded-full ring-1 ring-foreground-900/15"
                style={{ backgroundColor: s.color }}
              ></span>
            ))}
            {swatches.length > 4 && (
              <span className="text-[10.5px] text-foreground-600 ml-0.5">
                +{swatches.length - 4}
              </span>
            )}
          </div>
        )}

        {!isSoldOut && (() => {
          const inst = getInstallment(currentPrice);
          return inst ? (
            <p className="text-[11.5px] text-foreground-600 mt-0.5">
              {inst.count}x de {symbol}{formatPrice(inst.value)} sem juros
            </p>
          ) : null;
        })()}

        {isSoldOut ? (
          <span className="inline-flex items-center gap-1 mt-1 text-[12px] text-foreground-600">
            <i className="ri-mail-line text-[12px]"></i>
            Avise-me quando voltar
          </span>
        ) : isLowStock ? (
          <p className="text-[12px] text-primary-600 mt-1">
            Só restam {product.stock} — garanta já
          </p>
        ) : freeShip ? (
          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[11px] font-medium">
            Frete grátis
          </span>
        ) : hasDiscount ? (
          <span className="inline-flex items-center mt-1 text-[12px] text-foreground-600">
            Oferta termina hoje
          </span>
        ) : null}
      </div>
    </Link>
  );
}